import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ErrorDto } from 'src/app/shared/models/app.models';
import { BaseResponseDTO } from 'src/app/core/models/app.models';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { ApiService } from 'src/app/shared/services/api.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { environment } from 'src/environments/environment';
import { ValidateUserDTO } from 'src/app/modules/authentication/components/models/autentication-dto.models';
import { UserService } from 'src/app/shared/services/user.service';
import { lastValueFrom } from 'rxjs';
import en from 'src/assets/i18n/en';
import { FormValidationService } from 'src/app/shared/services/form-validation.service';
import { AuthenticationActions } from '../models/authentication-actions.enum';
import { ToastrService } from 'ngx-toastr';
import { MicrosoftAuthService } from 'src/app/shared/services/microsoft-auth.service';



@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['../../../authentication/components/authentication/authentication.component.scss']
})
export class LoginComponent implements OnInit {
  //#region properties

  //#region private
  private _loginForm!: FormGroup;
  invalidSubmit= false;
  errorsList : ErrorDto[] = [];
  returnUrl: string = '';
  translations = en;
  AppConstants = AppConstants;
  showPassword: boolean = false;
  isMicrosoftLoading: boolean = false;
  //#endregion

  //#region public
  //#endregion

  //#endregion

  //#region getters
  public get loginForm(): FormGroup {
    return this._loginForm;
  }
  //#endregion

  //#region setters
  public set loginForm(value: FormGroup) {
    this._loginForm = value;
  }
  //#endregion

  //#region ctor
  constructor(
    private _router: Router,
    private _activeRoute : ActivatedRoute,
    private _formBuilder: FormBuilder,
    private _http: HttpClient,
    private _dialogService: DialogService,
    private _loaderService: LoaderService,
    private _userService: UserService,
    private formValidationService: FormValidationService,
    private toastr: ToastrService,
    private microsoftAuthService: MicrosoftAuthService
  ) { }
  //#endregion

  //#region life cycle hooks
  ngOnInit(): void {
    this.initialize();
    this._activeRoute.queryParams.subscribe(params => this.returnUrl = params['returnUrl']);
  }
  //#endregion



  //#region private actions

  //#region API Service
  private createApiService(): ApiService {
    const apiService = new ApiService(this._http);
    apiService['_baseUrl'] = environment.API_URL; // Set the base URL manually
    return apiService;
  }
  //#endregion

  //#region Payloads
  private prepareValidateUserRequestPayload(data: any) {
    return data as ValidateUserDTO;
  }
  //#endregion

  //#region Fire HTTP Requests
  private fireValidateUserRequest(apiService: ApiService, payload: ValidateUserDTO) {
    let req$ = apiService.post(AppConstants.CONTROLLERS.USER, AppConstants.ACTIONS.AUTHENTICATE, true, environment.APIs_PREFIX, payload);
    return lastValueFrom(req$);
  }

  //#endregion

  //#region initialization
  private initializeForm() {
    this._loginForm = this._formBuilder.group({
      Email: [environment.DISABLE_BACKEND ? 'committee@demo.qa' : '', [Validators.required, Validators.email]],
      Password: [environment.DISABLE_BACKEND ? 'Demo@123' : '', [Validators.required, Validators.minLength(6)]]
    });
  }


  private initialize() {
    this.initializeForm();
  }
  //#endregion

  //#endregion

  //#region public actions

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
    });
  }

  //#region form
  public isFormValid() {
    return this.loginForm.valid;
  }

  public onSubmitForm(event: Event) {


    event.preventDefault();
    this.invalidSubmit = false;
    this.errorsList= [];

    if(this.isFormValid()) {
      let data = this.loginForm.getRawValue();
      this.handleValidateUser(data);
    } else {
      this.markFormGroupTouched(this.loginForm);
      this.invalidSubmit =true;


    }
  }

  //#region Handlers
  private async handleValidateUser(data: any) {
    console.log('handleValidateUser called with data:', data);
    this._loaderService.showLoader();
    
    try {
      const apiService = this.createApiService();
      const payload = this.prepareValidateUserRequestPayload(data);
      const response: any = await this.fireValidateUserRequest(apiService, payload);
      const result = response as unknown as BaseResponseDTO;
      
      if (result.isSuccess) {
        this._userService.email = data.Email;
        this._router.navigate(
          [AppConstants.ROUTES.AUTHENTICATION, AppConstants.ROUTES.OTP_Page],
          { queryParams: { action: AuthenticationActions.Login, returnUrl: this.returnUrl || null } }
        );
      } else {
        this.toastr.error(result.errorMessageEn || 'Login failed');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      this.invalidSubmit = true;
      this.errorsList = [];
      
      if (err.error?.errorMessageEn) {
        this.toastr.error(err.error.errorMessageEn);
      } else {
        this.toastr.error('An error occurred during login');
      }
    } finally {
      this._loaderService.hideLoader();
    }
  }
  //#endregion

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  async loginWithMicrosoft(): Promise<void> {
    this.isMicrosoftLoading = true;
    try {
      const success = await this.microsoftAuthService.loginWithPopup();
      if (success) {
        await this._router.navigateByUrl(this.returnUrl || `/${AppConstants.ROUTES.HOME}`);
      } else {
        this.toastr.error(this.microsoftAuthService.getLastErrorMessage() || 'Microsoft login failed.');
      }
    } finally {
      this.isMicrosoftLoading = false;
    }
  }

  //#endregion

  //#endregion
}
